import './account-type-selector.scss';

import { Modal } from '@basqet-dashboard/common/components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const ACCOUNT_TYPES = [
  {
    value: 'freelancer',
    label: 'Freelancer',
    description: 'Independent contractors and solo entrepreneurs'
  },
  {
    value: 'corporate_non_fi',
    label: 'Corporate Non-Financial',
    description: 'Registered standard business entities'
  },
  {
    value: 'corporate_fi',
    label: 'Corporate Financial',
    description: 'Regulated financial institutions'
  }
];

const MODAL_CONTENT = {
  freelancer: {
    title: 'Freelancer',
    description:
      'For individuals running a small business in their own name. You’re not registered as a company and you provide services as a one‑person business.',
    why: 'To confirm who you are and verify that you can use Basqet as an individual business.',
    needs: [
      'Valid government-issued ID (passport, driver’s licence, national ID card)',
      'Date of birth and contact details',
      'The address you use for your business',
      'Proof of address (utility bill or bank statement, not older than 3 months)',
      'BVN (11 digits)',
      'Platform link (website, social media, marketplace)',
      'Screenshot of platform or website profile'
    ]
  },
  corporate_non_fi: {
    title: 'Corporate Non-Financial Institution',
    description:
      'For registered companies that sell products or offer services. These businesses have official registration and operate as a recognized organization.',
    why: 'To verify your company is real, confirm who owns and runs it, and check that it meets the basic requirements for business KYB.',
    needs: [
      'Business registration documents ',
      'Registered business address',
      'Proof of operating address (utility bill, lease agreement or bank statement -no older than 3 months)',
      'Country or countries of operation',
      'Details of directors',
      'Official particular of directors',
      'Shareholders with 5 percent or more ownership',
      'Certified list of shareholders',
      'UBO (Ultimate Beneficial Owners) details',
      'IDs for UBOs',
      'Website link (if available)',
      'Details of every director, shareholder, UBO, management, and employee who is a government official or Politically Exposed Person (PEP)'
    ]
  },
  corporate_fi: {
    title: 'Corporate Financial Institution',
    description:
      'For organisations that offer financial services or fall under regulated categories such as DNFI (Designated Non‑Financial Institution) or DNFBP (Designated Non‑Financial Business and Profession). These businesses must meet stricter regulatory standards.',
    why: 'To confirm your organisation is licensed to operate, understand who is responsible for it, and make sure it meets the rules for financial‑sector businesses.',
    needs: [
      'Business registration documents ',
      'Registered business address',
      'Proof of operating address (utility bill, lease agreement or bank statement -no older than 3 months)',
      'Country or countries of operation',
      'Details of directors',
      'Official particular of directors',
      'Shareholders with 5 percent or more ownership',
      'Certified list of shareholders',
      'UBO (Ultimate Beneficial Owners) details',
      'IDs for UBOs',
      'Website link (if available)',
      'Details of every director, shareholder, UBO, management, and employee who is a government official or Politically Exposed Person (PEP)',
      'Applicable operating licences',
      'SCUML certificate (if applicable)',
      'AML/CFT policy document',
      'Additional information on Compliance practices'
    ]
  }
};

const AccountTypeSelector = ({ name, value, onChange, error }) => {
  const [activeModal, setActiveModal] = useState(null);

  const handleSelect = (selectedValue) => {
    onChange({
      target: {
        name,
        value: selectedValue
      }
    });
  };

  const modalContent = activeModal ? MODAL_CONTENT[activeModal] : null;

  return (
    <div className="account-type-selector">
      <div className="account-type-selector__options">
        {ACCOUNT_TYPES.map((type) => (
          <div
            key={type.value}
            className={classNames('account-type-selector__option', {
              'account-type-selector__option--selected': value === type.value
            })}
            onClick={() => handleSelect(type.value)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSelect(type.value);
              }
            }}>
            <div className="account-type-selector__radio">
              <div
                className={classNames('account-type-selector__radio-inner', {
                  'account-type-selector__radio-inner--checked': value === type.value
                })}
              />
            </div>
            <div className="account-type-selector__content">
              <span className="account-type-selector__label">{type.label}</span>
              <span className="account-type-selector__description">{type.description}</span>
              <button
                data-testid={`account-type-learn-more-${type.value}`}
                type="button"
                className="account-type-selector__learn-more"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveModal(type.value);
                }}>
                Learn more ›
              </button>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="account-type-selector__error">{error}</p>}

      {modalContent && (
        <Modal header={{ name: modalContent.title }} onModalClose={() => setActiveModal(null)}>
          <div className="account-type-selector__modal-outer">
            <p className="account-type-selector__modal-description">{modalContent.description}</p>
            <div className="account-type-selector__modal-inner">
              <div className="account-type-selector__modal-section">
                <h4 className="account-type-selector__modal-heading">
                  Why we collect this information
                </h4>
                <p className="account-type-selector__modal-text">{modalContent.why}</p>
              </div>
              <hr className="account-type-selector__modal-divider" />
              <div className="account-type-selector__modal-section">
                <h4 className="account-type-selector__modal-heading">
                  What we&apos;ll need from you
                </h4>
                <ul className="account-type-selector__modal-list">
                  {modalContent.needs.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

AccountTypeSelector.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

export { ACCOUNT_TYPES, AccountTypeSelector, MODAL_CONTENT };
